import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ThemeToggle } from '../../components/ThemeToggle';
import { Breadcrumbs } from '../../components/navigation/NavigationHelpers';
import { Store, Package, Star, ChevronLeft, MapPin, Phone, Mail } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function StoreDetailPage() {
  const { storeId } = useParams();
  const [store, setStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadStoreDetails();
  }, [storeId]);

  const loadStoreDetails = async () => {
    try {
      const [storeRes, productsRes] = await Promise.all([
        fetch(`${API_URL}/api/commerce/stores/${storeId}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/commerce/products?store_id=${storeId}`, { credentials: 'include' })
      ]);

      if (storeRes.ok) {
        const data = await storeRes.json();
        setStore(data.store || data);
      }

      if (productsRes.ok) {
        const data = await productsRes.json();
        setProducts(data.products || []);
      }
    } catch (error) {
      console.error('Failed to load store:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>Loading store...</p>
        </div>
      </div>
    );
  }

  if (!store) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <Card>
          <CardContent className="p-12 text-center">
            <Store className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600 mb-4">Store not found</p>
            <Button onClick={() => navigate('/commerce/marketplace')}>Back to Marketplace</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Theme Toggle */}
      <div className="fixed top-4 right-4 z-50">
        <ThemeToggle size="icon" className="bg-white dark:bg-gray-800 shadow-lg" />
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {/* Back Button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/commerce/marketplace')}
          className="mb-4"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Back to Marketplace
        </Button>

        {/* Breadcrumbs */}
        <Breadcrumbs items={[
          { label: 'Commerce', href: '/commerce/marketplace' },
          { label: store.store_name }
        ]} />

        {/* Store Header */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex items-start gap-6">
              <div className="bg-blue-100 dark:bg-blue-900 p-6 rounded-lg">
                <Store className="w-16 h-16 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{store.store_name}</h1>
                  {store.verified && <Badge variant="success">Verified</Badge>}
                  <Badge>{store.store_type}</Badge>
                </div>
                <p className="text-gray-600 dark:text-gray-400 mb-4">{store.description}</p>
                
                <div className="flex flex-wrap gap-4 text-sm text-gray-600 dark:text-gray-400">
                  {store.business_email && (
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      {store.business_email}
                    </div>
                  )}
                  {store.business_phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      {store.business_phone}
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Star className="w-4 h-4 text-yellow-500 fill-current" />
                    {store.rating?.toFixed(1) || '0.0'} ({store.total_reviews || 0} reviews)
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Store Stats */}
        <div className="grid md:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="text-center">
                <Package className="w-8 h-8 mx-auto mb-2 text-blue-500" />
                <p className="text-2xl font-bold">{products.length}</p>
                <p className="text-sm text-muted-foreground">Products</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-center">
                <Star className="w-8 h-8 mx-auto mb-2 text-yellow-500" />
                <p className="text-2xl font-bold">{store.total_sales || 0}</p>
                <p className="text-sm text-muted-foreground">Total Sales</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-center">
                <Store className="w-8 h-8 mx-auto mb-2 text-green-500" />
                <p className="text-2xl font-bold capitalize">{store.status}</p>
                <p className="text-sm text-muted-foreground">Store Status</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Products */}
        <Card>
          <CardHeader>
            <CardTitle>Products from {store.store_name}</CardTitle>
          </CardHeader>
          <CardContent>
            {products.length === 0 ? (
              <div className="text-center py-12">
                <Package className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600">No products available yet</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {products.map(product => (
                  <Card key={product.id} className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate(`/commerce/product/${product.id}`)}>
                    <div className="aspect-square bg-gray-100 flex items-center justify-center">
                      {product.images?.[0] ? (
                        <img src={product.images[0]} alt={product.title || product.name} className="w-full h-full object-cover" />
                      ) : (
                        <Package className="w-16 h-16 text-gray-400" />
                      )}
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-semibold line-clamp-1">{product.title || product.name}</h3>
                      <p className="text-sm text-gray-600 line-clamp-2 mt-1">{product.description}</p>
                      <div className="flex items-center justify-between mt-3">
                        <p className="text-lg font-bold text-blue-600">{product.price} {product.currency}</p>
                        {product.stock !== null && product.stock !== undefined && (
                          <Badge variant={product.stock > 0 ? 'default' : 'secondary'}>
                            {product.stock > 0 ? 'In Stock' : 'Out of Stock'}
                          </Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}